import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "tax-invoice-panel__check"
}
const _hoisted_2 = ["checked"]
const _hoisted_3 = {
  key: 1,
  class: "tax-invoice-panel__later"
}
const _hoisted_4 = { class: "tax-invoice-panel__notice" }
const _hoisted_5 = {
  class: "tax-invoice-panel__ready",
  "aria-label": "세금계산서 발행 준비"
}
const _hoisted_6 = { class: "tax-invoice-panel__fields" }
const _hoisted_7 = ["value"]
const _hoisted_8 = ["value"]
const _hoisted_9 = { class: "tax-invoice-panel__cert" }
const _hoisted_10 = { class: "tax-invoice-panel__upload" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = {
  key: 0,
  class: "tax-invoice-panel__msg tax-invoice-panel__msg--muted"
}
const _hoisted_13 = {
  key: 1,
  class: "tax-invoice-panel__msg tax-invoice-panel__msg--ok"
}
const _hoisted_14 = ["href"]
const _hoisted_15 = {
  key: 2,
  class: "tax-invoice-panel__msg tax-invoice-panel__msg--warn"
}
const _hoisted_16 = { class: "is-full" }
const _hoisted_17 = ["value"]
const _hoisted_18 = { class: "is-full tax-invoice-panel__confirm" }

import { computed, onMounted, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { saveMemberBizCert, uploadMemberBizCert, updateMemberContact } from '@/api/member'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { formatBizNumberInput, isEmailValid } from '@/utils/formValidation'


export default /*@__PURE__*/_defineComponent({
  __name: 'TaxInvoiceRequestPanel',
  props: {
    enabled: { type: Boolean, default: true },
    bizName: {},
    bizNum: {},
    email: {},
    showToggle: { type: Boolean, default: true },
    compact: { type: Boolean, default: false }
  },
  emits: ["update:enabled", "update:bizName", "update:bizNum", "update:email"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')

const props = __props

const emit = __emit

const { member, setMember } = useMemberAuth()

const bizCertUrl = ref('')
const bizCertUploading = ref(false)
const confirmed = ref(false)
const pendingBizCertUrl = ref('')

const active = computed(() => (props.showToggle ? props.enabled : true))

const emailReady = computed(() => isEmailValid(props.email))

const bizCertHref = computed(() =>
  bizCertUrl.value ? `${API_ORIGIN}${bizCertUrl.value}` : '',
)

function syncFromMember() {
  const m = member.value
  if (!m || !active.value) return

  if (!bizCertUrl.value && !pendingBizCertUrl.value) {
    bizCertUrl.value = m.bizCert || ''
  }
  if (!props.email.trim() && m.bossEmail) {
    emit('update:email', m.bossEmail)
  }
  if (!props.bizName.trim() && m.orgName) {
    emit('update:bizName', m.orgName)
  }
  if (!props.bizNum.trim() && m.bizNum) {
    emit('update:bizNum', formatBizNumberInput(m.bizNum))
  }
}

watch(
  () => member.value?.bizCert,
  (value) => {
    if (value && !pendingBizCertUrl.value) {
      bizCertUrl.value = value
    }
  },
  { immediate: true },
)

watch(
  active,
  (value) => {
    if (value) syncFromMember()
    else confirmed.value = false
  },
  { immediate: true },
)

onMounted(() => {
  syncFromMember()
})

function onToggle(event: Event) {
  const checked = (event.target as HTMLInputElement).checked
  emit('update:enabled', checked)
  if (checked) syncFromMember()
}

async function onBizCertChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  bizCertUploading.value = true
  try {
    const uploaded = await uploadMemberBizCert(file)
    pendingBizCertUrl.value = uploaded.url
    bizCertUrl.value = uploaded.url
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '사업자등록증 업로드에 실패했습니다.')
  } finally {
    bizCertUploading.value = false
    input.value = ''
  }
}

function validate(): string | null {
  if (!active.value) return null

  if (!props.bizName.trim()) {
    return '세금계산서 상호를 입력해 주세요.'
  }
  if (props.bizNum.replace(/\D/g, '').length < 10) {
    return '세금계산서 사업자번호를 입력해 주세요.'
  }
  if (!bizCertUrl.value) {
    return '세금계산서 발행을 위해 사업자등록증을 업로드해 주세요.'
  }
  if (!props.email.trim()) {
    return '세금계산서를 받을 이메일을 입력해 주세요.'
  }
  if (!isEmailValid(props.email)) {
    return '이메일 형식을 확인해 주세요.'
  }
  if (!confirmed.value) {
    return '사업자등록증과 이메일 확인에 동의해 주세요.'
  }
  return null
}

async function persist(): Promise<void> {
  if (!active.value) return

  if (pendingBizCertUrl.value) {
    const updated = await saveMemberBizCert(pendingBizCertUrl.value)
    pendingBizCertUrl.value = ''
    if (updated) setMember(updated)
  }

  const updated = await updateMemberContact({ email: props.email.trim() })
  if (updated) setMember(updated)
}

function confirmMessage(): string {
  return `세금계산서를 ${props.email.trim()}로 보내드립니다.\n사업자등록증과 이메일이 맞는지 다시 확인해 주세요.`
}

__expose({
  validate,
  persist,
  confirmMessage,
  hasBizCert: () => Boolean(bizCertUrl.value),
  hasEmail: () => emailReady.value,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["tax-invoice-panel", { 'is-compact': __props.compact }])
  }, [
    (__props.showToggle)
      ? (_openBlock(), _createElementBlock("label", _hoisted_1, [
          _createElementVNode("input", {
            type: "checkbox",
            checked: __props.enabled,
            onChange: _cache[0] || (_cache[0] = ($event: any) => (onToggle($event)))
          }, null, 40, _hoisted_2),
          _cache[5] || (_cache[5] = _createElementVNode("span", null, "세금계산서 발행 요청", -1))
        ]))
      : _createCommentVNode("", true),
    (__props.showToggle)
      ? (_openBlock(), _createElementBlock("p", _hoisted_3, " 지금 요청하지 않아도, 입금 확인 후 주문상세에서 요청할 수 있습니다. "))
      : _createCommentVNode("", true),
    (active.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(AppIcon, {
              icon: "mdi-file-document-outline",
              size: 18
            }),
            _cache[6] || (_cache[6] = _createElementVNode("p", null, [
              _createTextVNode(" 세금계산서 발행을 위해 "),
              _createElementVNode("strong", null, "사업자등록증"),
              _createTextVNode("과 "),
              _createElementVNode("strong", null, "수신 이메일"),
              _createTextVNode("이 필요합니다. "),
              _createElementVNode("strong", null, "마이페이지로 가지 않아도"),
              _createTextVNode(" 아래에서 바로 확인·등록할 수 있습니다. ")
            ], -1))
          ]),
          _createElementVNode("ul", _hoisted_5, [
            _createElementVNode("li", {
              class: _normalizeClass({ 'is-ok': Boolean(bizCertUrl.value) })
            }, [
              _createVNode(AppIcon, {
                icon: bizCertUrl.value ? 'mdi-check-circle' : 'mdi-alert-circle-outline',
                size: 16
              }, null, 8, ["icon"]),
              _createTextVNode(" 사업자등록증 " + _toDisplayString(bizCertUrl.value ? '등록됨' : '업로드 필요'), 1)
            ], 2),
            _createElementVNode("li", {
              class: _normalizeClass({ 'is-ok': emailReady.value })
            }, [
              _createVNode(AppIcon, {
                icon: emailReady.value ? 'mdi-check-circle' : 'mdi-alert-circle-outline',
                size: 16
              }, null, 8, ["icon"]),
              _createTextVNode(" 수신 이메일 " + _toDisplayString(emailReady.value ? '입력됨' : '입력 필요'), 1)
            ], 2)
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("label", null, [
              _cache[7] || (_cache[7] = _createElementVNode("span", null, "상호", -1)),
              _createElementVNode("input", {
                value: __props.bizName,
                type: "text",
                maxlength: "100",
                placeholder: "상호",
                onInput: _cache[1] || (_cache[1] = ($event: any) => (emit('update:bizName', ($event.target as HTMLInputElement).value.trim())))
              }, null, 40, _hoisted_7)
            ]),
            _createElementVNode("label", null, [
              _cache[8] || (_cache[8] = _createElementVNode("span", null, "사업자번호", -1)),
              _createElementVNode("input", {
                value: __props.bizNum,
                maxlength: "12",
                inputmode: "numeric",
                placeholder: "000-00-00000",
                onInput: _cache[2] || (_cache[2] = ($event: any) => (emit('update:bizNum', _unref(formatBizNumberInput)(($event.target as HTMLInputElement).value))))
              }, null, 40, _hoisted_8)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _cache[10] || (_cache[10] = _createElementVNode("span", null, "사업자등록증", -1)),
              _createElementVNode("label", _hoisted_10, [
                _createElementVNode("input", {
                  type: "file",
                  accept: ".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf",
                  disabled: bizCertUploading.value,
                  onChange: onBizCertChange
                }, null, 40, _hoisted_11),
                _createElementVNode("span", null, _toDisplayString(bizCertUrl.value ? '다시 업로드' : '파일 선택'), 1)
              ]),
              (bizCertUploading.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_12, " 업로드 중… "))
                : (bizCertUrl.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_13, [
                      _cache[9] || (_cache[9] = _createTextVNode(" 등록증이 등록되어 있습니다. ", -1)),
                      _createElementVNode("a", {
                        href: bizCertHref.value,
                        target: "_blank",
                        rel: "noopener",
                        class: "tax-invoice-panel__link"
                      }, " 미리보기 ", 8, _hoisted_14)
                    ]))
                  : (_openBlock(), _createElementBlock("p", _hoisted_15, " 사업자등록증을 업로드해 주세요. (jpg · png · webp · pdf, 최대 10MB) "))
            ]),
            _createElementVNode("label", _hoisted_16, [
              _cache[11] || (_cache[11] = _createElementVNode("span", null, "세금계산서 수신 이메일", -1)),
              _createElementVNode("input", {
                value: __props.email,
                type: "email",
                maxlength: "100",
                placeholder: "example@company.com",
                onInput: _cache[3] || (_cache[3] = ($event: any) => (emit('update:email', ($event.target as HTMLInputElement).value.trim())))
              }, null, 40, _hoisted_17),
              _cache[12] || (_cache[12] = _createElementVNode("em", { class: "tax-invoice-panel__hint" }, "발행된 세금계산서를 이 주소로 보내드립니다.", -1))
            ]),
            _createElementVNode("label", _hoisted_18, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((confirmed).value = $event)),
                type: "checkbox"
              }, null, 512), [
                [_vModelCheckbox, confirmed.value]
              ]),
              _createElementVNode("span", null, [
                _cache[13] || (_cache[13] = _createTextVNode(" 사업자등록증과 이메일(", -1)),
                _createElementVNode("strong", null, _toDisplayString(__props.email || '미입력'), 1),
                _cache[14] || (_cache[14] = _createTextVNode(")을 확인했습니다. 세금계산서를 위 주소로 받겠습니다. ", -1))
              ])
            ])
          ])
        ], 64))
      : _createCommentVNode("", true)
  ], 2))
}
}

})